import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import JsonLd from '@/components/base/JsonLd';

const siteUrl = (import.meta.env.VITE_SITE_URL || 'https://yoybuy.cyou').replace(/\/$/, '');
const pagePath = '/yoybuy-spreadsheet-accessories';

const articleSchema = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: 'Аксессуары из Китая через YOYbuy Spreadsheet: очки, часы, украшения и ремни — полный каталог',
  description: 'Всё о заказе аксессуаров из Китая с помощью yoybuy spreadsheet. Как выбрать качественные очки, часы, бижутерию и ремни, не переплачивая за бренд.',
  author: { '@type': 'Organization', name: 'YOYbuy Sheet' },
  publisher: {
    '@type': 'Organization',
    name: 'YOYbuy Sheet',
    logo: {
      '@type': 'ImageObject',
      url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/67a09e300463e31c534fce7fbe7473f7.jpeg',
    },
  },
  datePublished: '2026-07-05',
  dateModified: '2026-07-05',
  url: `${siteUrl}${pagePath}`,
  image: 'https://readdy.ai/api/search-image?query=Minimalist%20flat%20lay%20photography%20of%20elegant%20fashion%20accessories%20including%20a%20gold%20tone%20watch%2C%20tortoiseshell%20sunglasses%2C%20a%20braided%20leather%20belt%2C%20and%20a%20delicate%20silver%20necklace%20arranged%20on%20a%20cream%20linen%20surface%2C%20soft%20natural%20daylight%2C%20warm%20neutral%20tones%2C%20editorial%20jewelry%20photography%2C%20clean%20aesthetic%20composition%2C%20high%20detail%20on%20metal%20textures%20and%20reflections&width=1400&height=700&seq=article-accessories-hero-2026&orientation=landscape',
  inLanguage: 'ru',
  isAccessibleForFree: true,
};

export default function AccessoriesArticle() {
  return (
    <>
      <JsonLd data={articleSchema} />
      <Navbar />
      <main className="pt-20 md:pt-24">
        <article className="py-12 md:py-16 bg-background-50">
          <div className="w-full px-6 md:px-10">
            <div className="max-w-3xl mx-auto">
              <header className="mb-10">
                <div className="flex items-center gap-2 text-sm text-foreground-500 mb-4">
                  <Link to="/" className="hover:text-primary-600 transition-colors">Главная</Link>
                  <span>/</span>
                  <span className="text-foreground-700">Аксессуары</span>
                </div>
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-950 leading-tight">
                  Аксессуары из Китая через YOYbuy Spreadsheet: очки, часы, украшения и ремни — полный каталог
                </h1>
                <p className="mt-4 text-sm md:text-base text-foreground-500">
                  Всё о заказе аксессуаров из Китая с помощью <strong>yoybuy spreadsheet</strong>. Как выбрать качественные очки, часы, бижутерию и ремни, не переплачивая за бренд.
                </p>
                <div className="mt-6 flex items-center gap-4 text-xs text-foreground-400">
                  <span>Обновлено: Июль 2026</span>
                  <span>·</span>
                  <span>Время чтения: 7 минут</span>
                </div>
              </header>

              <div className="mb-10 rounded-2xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Minimalist%20flat%20lay%20photography%20of%20elegant%20fashion%20accessories%20including%20a%20gold%20tone%20watch%2C%20tortoiseshell%20sunglasses%2C%20a%20braided%20leather%20belt%2C%20and%20a%20delicate%20silver%20necklace%20arranged%20on%20a%20cream%20linen%20surface%2C%20soft%20natural%20daylight%2C%20warm%20neutral%20tones%2C%20editorial%20jewelry%20photography%2C%20clean%20aesthetic%20composition%2C%20high%20detail%20on%20metal%20textures%20and%20reflections&width=1400&height=700&seq=article-accessories-hero-2026&orientation=landscape"
                  alt="Заказ аксессуаров из Китая через YOYbuy Sheet"
                  title="YOYbuy Sheet аксессуары — очки, часы, украшения из Китая"
                  className="w-full h-auto object-cover"
                />
              </div>

              <div className="prose-custom space-y-8 text-sm md:text-base text-foreground-700 leading-relaxed">
                <section>
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                    Аксессуары из Китая: почему это выгодно и как не ошибиться
                  </h2>
                  <p>
                    Аксессуары — идеальная категория для заказа из Китая. Маленький вес, низкая стоимость доставки, огромный ассортимент. От солнцезащитных очков до кожаных ремней, от минималистичных часов до дизайнерской бижутерии — китайские производители предлагают всё. А <strong>yoybuy spreadsheet</strong> помогает систематизировать заказ так, что вы получаете именно то, что хотели — без сюрпризов с качеством.
                  </p>
                </section>

                <section>
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                    Топ-5 категорий аксессуаров в YOYbuy Sheet
                  </h2>
                  <div className="space-y-4">
                    {[
                      { name: 'Солнцезащитные очки', desc: 'Важно указать в yoybuy sheet форму оправы (авиаторы, круглые, квадратные), материал линз и наличие UV-защиты. Попросите агента проверить поляризацию — это ключевой параметр качества.' },
                      { name: 'Часы', desc: 'От quartz до автоматических механизмов. В таблице YOYbuy укажите тип механизма, материал корпуса и браслета, водозащиту. Китайские часовые фабрики предлагают отличное качество по доступным ценам.' },
                      { name: 'Бижутерия и украшения', desc: 'Кольца, цепочки, серьги, браслеты. В yoybuy spreadsheet отметьте материал (серебро, сталь, латунь), тип покрытия и наличие камней. Агент проверит качество застёжек и покрытия перед отправкой.' },
                      { name: 'Ремни', desc: 'Кожаные и тканевые. Укажите в таблице ширину, длину, тип пряжки и цвет. Китайские кожаные ремни часто не уступают европейским аналогам при цене в 3-5 раз ниже.' },
                      { name: 'Головные уборы', desc: 'Кепки, шапки, панамы. В yoybuy sheet укажите размер (обхват головы), материал и желаемый фасон. Лёгкие и компактные — доставка стоит копейки.' },
                    ].map((item) => (
                      <div key={item.name} className="flex items-start gap-3 p-4 rounded-lg bg-background-100 border border-background-200">
                        <span className="w-1.5 h-1.5 mt-2 rounded-full bg-accent-500 flex-shrink-0"></span>
                        <div>
                          <span className="text-sm font-semibold text-foreground-900">{item.name}</span>
                          <p className="text-xs text-foreground-500 mt-1">{item.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>

                <div className="my-8 rounded-xl overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Elegant%20product%20photography%20of%20a%20minimalist%20wristwatch%20with%20a%20brown%20leather%20strap%20placed%20on%20a%20stack%20of%20neutral%20toned%20fashion%20magazines%2C%20soft%20window%20light%20creating%20a%20warm%20glow%2C%20cream%20and%20beige%20color%20scheme%2C%20visible%20watch%20face%20details%2C%20editorial%20lifestyle%20photography%2C%20clean%20minimal%20aesthetic%2C%20high%20detail%20on%20metal%20and%20leather%20textures&width=1200&height=600&seq=article-accessories-watches&orientation=landscape"
                    alt="YOYbuy Sheet — аксессуары: часы, очки, бижутерия"
                    title="Заказ аксессуаров через YOYbuy Spreadsheet из Китая"
                    className="w-full h-auto object-cover"
                  />
                </div>

                <section>
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                    Как заказывать аксессуары оптом через YOYbuy Spreadsheet
                  </h2>
                  <p>
                    Одна из фишек <strong>yoybuy sheet</strong> — возможность собрать много мелких аксессуаров в один заказ. 10 пар очков, 5 ремней, 3 пары часов и горсть бижутерии — всё это помещается в одну посылку, а стоимость доставки распределяется на каждый товар. Таблица автоматически суммирует вес и рассчитывает итоговую сумму — просто укажите каждый товар отдельной строкой, а <strong>yoybuy spreadsheet</strong> сделает все расчёты.
                  </p>
                </section>

                <section>
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                    Заключение
                  </h2>
                  <p>
                    Заказ аксессуаров через <strong>yoybuy sheet</strong> — это доступ к бесконечному ассортименту по ценам производителя. Маленький вес и низкая стоимость доставки делают эту категорию одной из самых выгодных для заказа из Китая. <Link to="/" className="text-primary-600 hover:text-primary-700 underline font-medium">Вернитесь на главную yoybuy.cyou</Link> и откройте для себя мир аксессуаров. Читайте также: <Link to="/yoybuy-spreadsheet-bags" className="text-primary-600 hover:text-primary-700 underline font-medium">сумки из Китая</Link> и <Link to="/yoybuy-sheet-guide" className="text-primary-600 hover:text-primary-700 underline font-medium">руководство по YOYbuy Sheet</Link>.
                  </p>
                </section>
              </div>

              <div className="mt-12 pt-8 border-t border-background-300">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div>
                    <p className="text-sm text-foreground-500">Было полезно? Поделитесь:</p>
                    <div className="flex items-center gap-3 mt-2">
                      <a href="#" className="w-9 h-9 rounded-full bg-background-200 flex items-center justify-center text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer">
                        <i className="ri-twitter-x-line"></i>
                      </a>
                      <a href="#" className="w-9 h-9 rounded-full bg-background-200 flex items-center justify-center text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer">
                        <i className="ri-telegram-line"></i>
                      </a>
                      <a href="#" className="w-9 h-9 rounded-full bg-background-200 flex items-center justify-center text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer">
                        <i className="ri-facebook-line"></i>
                      </a>
                    </div>
                  </div>
                  <Link
                    to="/"
                    className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium rounded-full bg-primary-500 text-white hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-arrow-left-line"></i>
                    Вернуться на главную
                  </Link>
                </div>
              </div>

              <div className="mt-10 p-6 rounded-xl bg-background-100 border border-background-200">
                <p className="text-sm font-semibold text-foreground-900 mb-2">Читайте также:</p>
                <div className="flex flex-wrap gap-2">
                  <Link to="/yoybuy-sheet-guide" className="text-xs text-primary-600 hover:text-primary-700 underline">Руководство по YOYbuy Sheet</Link>
                  <span className="text-xs text-foreground-400">·</span>
                  <Link to="/yoybuy-spreadsheet-bags" className="text-xs text-primary-600 hover:text-primary-700 underline">Сумки из Китая</Link>
                  <span className="text-xs text-foreground-400">·</span>
                  <Link to="/yoybuy-spreadsheet-shoes" className="text-xs text-primary-600 hover:text-primary-700 underline">Заказ обуви</Link>
                  <span className="text-xs text-foreground-400">·</span>
                  <Link to="/" className="text-xs text-primary-600 hover:text-primary-700 underline">Главная</Link>
                  <span className="text-xs text-foreground-400">·</span>
                  <a href="https://kaspreadsheet.com/search-920/name/Shoes.html" target="_blank" rel="noopener nofollow" className="text-xs text-primary-600 hover:text-primary-700 underline">Аксессуары на kaspreadsheet</a>
                </div>
              </div>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}